import { useQuery } from '@apollo/client';
import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import useTravelUser from '@flight-workspace/common-client/src/hooks/useTravelUser';
import {
  useKoreanUnitSeparators,
  withPriceSeperator,
} from '@flight-workspace/common-client/src/util/number';
import classNames from 'classnames/bind';
import { IPromotion } from 'common/promotion';
import { useToast } from 'common/searchBox/Toast';
import { isTravelClub } from 'common/travelLabel';
import { keyValueArrayToHashMap } from 'common/travelLabel/utils';
import { IKeyStringValue } from 'common/types';
import { getNpayLabelInfo } from 'common/utils';
import config from 'config/index';
import useFlightsCards from 'hooks/useFlightsCards';
import PromotionInfo from 'list/common/promotionInfo';
import Travelclub from 'list/common/travelclub';
import { IFareTypeList, IScheduleDetail } from 'list/international/types';
import React, { Dispatch, SetStateAction, useState } from 'react';
import GET_BOOKING_PARTNERS from '../common/travelLabel/bookingPartners.gql';
import styles from './index.module.scss';
import getPromotions from './promotion';
import { ICardFareTypeMap, IPassengerCount } from './types';
import {
  areaCode,
  getLowPriceMap,
  setChecked,
  setChecklistToggleAll,
} from './utils';

const cx = classNames.bind(styles);

interface IOtherCardFareTypeProps {
  departureDate: string;
  korFareTypes: IKeyStringValue;
  passengerCount: IPassengerCount;
  prevCardFareMap: ICardFareTypeMap;
  promotions: IPromotion[];
  selectedCardFares: IFareTypeList;
  selectedSchedules: IScheduleDetail[][];
  closeModal: () => void;
  restoreScroll: () => void;
  setCheckCardFareOption: React.Dispatch<any>;
  setIsMore: Dispatch<SetStateAction<boolean>>;
}

const OtherCardFareType = ({
  departureDate,
  korFareTypes,
  passengerCount,
  prevCardFareMap,
  promotions,
  selectedCardFares,
  selectedSchedules,
  closeModal,
  restoreScroll,
  setCheckCardFareOption,
  setIsMore,
}: IOtherCardFareTypeProps) => {
  const [checkedList, setCheckedList] = useState(prevCardFareMap);
  const { isLogin, login, userTravelClub } = useTravelUser({
    nAuthWithUrlParam: config.nAuthWithUrlParam,
  });
  const { getSavingRate } = userTravelClub;
  const { data: bookingPartnerData } = useQuery(GET_BOOKING_PARTNERS);
  const bookingPartnersHashMap = keyValueArrayToHashMap(
    bookingPartnerData?.bookingPartners,
    'otaId',
    'expName',
  );
  const cardFareTypeList = getLowPriceMap(selectedCardFares, passengerCount);
  const onCancel = () => {
    setCheckCardFareOption(prevCardFareMap);
    closeModal();
    restoreScroll();
  };
  const onSubmit = () => {
    setCheckCardFareOption(checkedList);
    closeModal();
    restoreScroll();
    setIsMore(false); // 여행사 더보기
  };
  const ref = useOffClick(() => {
    closeModal();
    restoreScroll();
  });

  const flightCards = useFlightsCards();
  const [showNpayLabelPopup, setShowNpayLabelPopup] = useToast();

  // 기본
  // - 공통: 트래블클럽 로고 노출
  // - 비로그인: 로그인 버튼 노출
  // - 로그인: 최저가 노출
  const defaultFareListItem = (
    isTravelClub: boolean,
    isPromotion: boolean,
    price: number,
  ) => {
    return (
      <>
        {/* 적립 프로모션 중이면 비로그인 이라도 요금이 노출 됨 */}
        {isTravelClub && !isLogin && !isPromotion ? (
          <a
            className={cx('login')}
            href="#"
            itemProp="offers"
            onClick={() => login(window.location.href)}
          >
            <span className={cx('txt')}>특가확인</span>
            <i className={cx('nlogin')} title="NAVER 로그인" />
          </a>
        ) : (
          <b className={cx('usual')} itemProp="offers">
            <i className={cx('num')}>{withPriceSeperator(price)}</i>
            <span className={cx('unit')}>원~</span>
          </b>
        )}
      </>
    );
  };

  // 즉시할인 프로모션
  // 트래블클럽(할인요금) 존재시
  // 로그인/비로그인 UI 동일
  const directPromotionListItem = (price: number, naverFare: number) => {
    return (
      <>
        <del className={cx('before')} itemProp="offers">
          <b className={cx('num')}>{withPriceSeperator(price)}</b>
          <span className={cx('unit')}>원~</span>
        </del>
        <div className={cx('condition')} itemProp="itemCondition">
          <span className={cx('directpromotion')}>
            {useKoreanUnitSeparators(price - naverFare)}원 할인
          </span>
        </div>
        <b className={cx('promoted')} itemProp="offers">
          <i className={cx('num')}>{withPriceSeperator(naverFare)}</i>
          <span className={cx('unit')}>원~</span>
        </b>
      </>
    );
  };

  return (
    <div className={cx('OtherCardFareType')} ref={ref}>
      <div className={cx('header')}>
        <b className={cx('title')}>다른 요금 조건</b>
        <button
          className={cx('close')}
          type="button"
          aria-label="닫기"
          data-event-nlog="click"
          data-event-area-code={`${areaCode}.faretypeclose`}
          onClick={onCancel}
        />
      </div>
      <div className={cx('tabpane')}>
        <div className={cx('types')}>
          <div className={cx('type')} data-checked={checkedList.all}>
            <button
              className={cx('button')}
              type="button"
              role="checkbox"
              aria-checked={checkedList.all}
              value="all"
              data-event-nlog="click"
              data-event-area-code={`${areaCode}.faretypeall`}
              onClick={() => setChecklistToggleAll(checkedList, setCheckedList)}
            />
            <i className={cx('name')} itemProp="name">
              전체
            </i>
          </div>

          {cardFareTypeList.map(
            (
              { key, otaId, price, fareDetail, naverFare, hasTravelClubFare },
              idx: number,
            ) => {
              const isDiscountPromotion = naverFare > 0;
              const basePrice = isDiscountPromotion ? naverFare : price;
              const {
                promotions: [promotion],
              } = getPromotions(
                promotions,
                selectedSchedules,
                fareDetail,
                passengerCount,
                basePrice,
              );
              const isNPayPromotion = isDiscountPromotion && !!promotion;
              const travelClubSavingRate =
                isTravelClub({
                  grade: userTravelClub.grade,
                  otaId,
                  bookingPartnersHashMap,
                }) && getSavingRate('flight', departureDate, otaId, basePrice);

              const nPayLabelInfo = getNpayLabelInfo(
                fareDetail.FareType,
                flightCards,
              );
              return (
                <div
                  key={`item_${idx}`}
                  className={cx('type')}
                  itemScope
                  data-withmembership={!!hasTravelClubFare}
                  data-checked={checkedList[key]}
                >
                  <button
                    className={cx('button')}
                    type="button"
                    role="checkbox"
                    aria-checked={checkedList[key]}
                    data-event-nlog="click"
                    data-event-area-code={`${areaCode}.faretypesel`}
                    onClick={() => {
                      if (!isLogin && hasTravelClubFare) {
                        login(window.location.href);
                      } else {
                        setChecked(checkedList, key, setCheckedList);
                      }
                    }}
                  />
                  <i
                    className={cx('name')}
                    itemProp="name"
                  >{`${korFareTypes[key]}`}</i>
                  {hasTravelClubFare && (
                    <div className={cx('condition')} itemProp="itemCondition">
                      <span className={cx('travelclub_discount')}>
                        <i className={cx('ico')} title="네이버 트래블클럽" />
                        할인
                      </span>
                    </div>
                  )}
                  {isDiscountPromotion
                    ? directPromotionListItem(price, naverFare)
                    : defaultFareListItem(
                        hasTravelClubFare,
                        isNPayPromotion,
                        price,
                      )}
                  {isNPayPromotion && (
                    <div
                      className={cx('condition')}
                      itemProp="itemCondition"
                      role="row"
                    >
                      <div className={cx('promotion')}>
                        <span className={cx('desc')}>
                          <PromotionInfo
                            npayIcon={<i className={cx('npay')} title="npay" />}
                            paymentMethod={
                              promotion.paymentMethod &&
                              korFareTypes[key].split('/')[1]
                            }
                            summaryByType={promotion.summaryByType}
                          />
                        </span>
                        <b className={cx('price')}>
                          <i className={cx('num')}>
                            {withPriceSeperator(promotion.discountedPrice)}
                          </i>
                          <span className={cx('unit')}>원~</span>
                        </b>
                      </div>
                    </div>
                  )}
                  {!!nPayLabelInfo && (
                    <div
                      className={cx('condition')}
                      itemProp="itemCondition"
                      role="row"
                    >
                      <button
                        className={cx('label_npay')}
                        onClick={() =>
                          setShowNpayLabelPopup(
                            showNpayLabelPopup ? '' : 'show',
                          )
                        }
                        onMouseEnter={() =>
                          setShowNpayLabelPopup(
                            showNpayLabelPopup ? '' : 'show',
                          )
                        }
                        aria-haspopup="true"
                        aria-expanded={!!showNpayLabelPopup}
                      >
                        <i className={cx('label')} aria-label="네이버페이" />
                        {`${nPayLabelInfo.nPayPromotionAmount}% 적립`}
                      </button>
                      <div className={cx('popup')}>
                        {nPayLabelInfo.nPayLabelPopupText}
                      </div>
                    </div>
                  )}
                  {!!travelClubSavingRate && (
                    <div
                      className={cx('condition')}
                      itemProp="itemCondition"
                      role="row"
                    >
                      <Travelclub
                        savingRate={travelClubSavingRate}
                        isMemberFare={hasTravelClubFare}
                      />
                    </div>
                  )}
                </div>
              );
            },
          )}
        </div>
      </div>
      <div className={cx('footer')}>
        <div className={cx('btns')}>
          <button
            className={cx('cancel')}
            type="button"
            data-event-nlog="click"
            data-event-area-code={`${areaCode}.faretypecancel`}
            onClick={onCancel}
          >
            취소
          </button>
          <button
            className={cx('submit')}
            type="submit"
            data-event-nlog="click"
            data-event-area-code={`${areaCode}.faretypeapply`}
            onClick={onSubmit}
          >
            적용
          </button>
        </div>
      </div>
    </div>
  );
};

export default OtherCardFareType;
